// Shared, server-safe video types and helpers. Kept out of the 'use client'
// gallery so server components can build JSON-LD from the same data.

/**
 * Who made the video. Required on every card so an embed can never render
 * without telling the reader whose interests it serves.
 */
export type VideoSource =
  | { kind: 'official'; vendor: string }
  | { kind: 'independent' }
  | { kind: 'consultancy'; name: string }
  | { kind: 'sponsored'; sponsor: string }
  | { kind: 'competitor'; maker: string };

export interface KeyMoment {
  /** Seconds from the start. */
  t: number;
  /** Our own summary of what happens at this point, never a transcript quote. */
  label: string;
}

export interface GalleryVideo {
  id: string;
  title: string;
  channel: string;
  /** ISO 8601 date the video was published (from the YouTube Data API at authoring time). */
  uploadDate: string;
  /** ISO 8601 duration, e.g. "PT5M48S" (from the YouTube Data API at authoring time). */
  duration: string;
  /** Our description, used on the card and in VideoObject JSON-LD. */
  description: string;
  source: VideoSource;
  moments?: KeyMoment[];
}

export function badgeLabel(source: VideoSource): string {
  switch (source.kind) {
    case 'official':
      return `Official: ${source.vendor}`;
    case 'independent':
      return 'Independent';
    case 'consultancy':
      return `Made by a consultancy (${source.name})`;
    case 'sponsored':
      return `Sponsored by ${source.sponsor}`;
    case 'competitor':
      return `Made by a competitor (${source.maker})`;
  }
}

export function formatClock(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/** "PT1H2M3S" -> seconds. */
export function isoDurationSeconds(iso: string): number {
  const m = iso.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/);
  if (!m) return 0;
  return Number(m[1] || 0) * 3600 + Number(m[2] || 0) * 60 + Number(m[3] || 0);
}

/** schema.org VideoObject for one gallery video. Serialize with lib/jsonld's jsonLd(). */
export function videoObjectJsonLd(v: GalleryVideo) {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: v.title,
    description: v.description,
    thumbnailUrl: [`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`],
    uploadDate: v.uploadDate,
    duration: v.duration,
    embedUrl: `https://www.youtube-nocookie.com/embed/${v.id}`,
    contentUrl: `https://www.youtube.com/watch?v=${v.id}`,
    author: { '@type': 'Organization', name: v.channel },
  };
}
